/**
 * Pattern A: every audible attack is caused by one epicycle crossing a fixed
 * LOCAL angle. The visual flashes and synthesized PCM share this event list.
 * This is an independent Fourier-series rhythm experiment, not a DFT of an animal.
 */
export const TAU = Math.PI * 2;
export const DURATION = 12;
export const PERIOD = 2.4;

export type Ring = Readonly<{
  frequency: number;
  radius: number;
  phase: number;
  color: string;
  enter: number;
  markers: number;
  pitch: number;
  timbre: "wood" | "marimba" | "glass" | "click";
}>;

export const RINGS: readonly Ring[] = [
  { frequency: 1, radius: 200, phase: -0.15, color: "#25D7C2", enter: 0, markers: 4, pitch: 180, timbre: "wood" },
  { frequency: 2, radius: 98, phase: -1.2, color: "#A08BFF", enter: 2.4, markers: 1, pitch: 320, timbre: "marimba" },
  { frequency: 3, radius: 61, phase: 1.6, color: "#FFCA69", enter: 4.8, markers: 1, pitch: 660, timbre: "glass" },
  { frequency: 5, radius: 34, phase: 0.8, color: "#FF7FA3", enter: 7.2, markers: 1, pitch: 1250, timbre: "click" },
];

export type Beat = Readonly<{
  time: number;
  ringIndex: number;
  markerIndex: number;
  angle: number;
  id: string;
}>;

export const smoothstep = (t: number) => {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
};

export const ringWeight = (ring: Ring, time: number) =>
  ring.enter === 0 ? 1 : smoothstep((time - ring.enter) / 0.36);

/** Absolute-time oscillator: the phase never resets at a new layer. */
export const angleAt = (ring: Ring, time: number) =>
  TAU * ring.frequency * time / PERIOD + ring.phase;

export const chainAt = (time: number) => {
  let x = 0;
  let y = 0;
  return RINGS.map((ring) => {
    const cx = x;
    const cy = y;
    const a = angleAt(ring, time);
    const r = ring.radius * ringWeight(ring, time);
    x += r * Math.cos(a);
    y += r * Math.sin(a);
    return { cx, cy, x, y, r, a };
  });
};

export const BEATS: readonly Beat[] = RINGS.flatMap((ring, ringIndex) => {
  const beats: Beat[] = [];
  for (let markerIndex = 0; markerIndex < ring.markers; markerIndex++) {
    const angle = TAU * markerIndex / ring.markers;
    for (let turn = -40; turn <= 40; turn++) {
      const time = PERIOD * (angle - ring.phase + TAU * turn) / (TAU * ring.frequency);
      // Never play an invisible ring, and avoid a click on the exact frame 0.
      const firstSound = ring.enter + (ringIndex === 0 ? 0.015 : 0.36);
      if (time >= firstSound && time < DURATION - 0.12) {
        beats.push({ time, ringIndex, markerIndex, angle, id: `${ringIndex}:${markerIndex}:${turn}` });
      }
    }
  }
  return beats;
}).sort((a, b) => a.time - b.time || a.ringIndex - b.ringIndex);

export const beatAtTime = (beat: Beat) => {
  const c = chainAt(beat.time)[beat.ringIndex];
  return { x: c.x, y: c.y };
};
